import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  listEmployees,
  listWorkflowMonths,
  loadWorkflowData,
  resolveCurrentMonth,
  type EmployeeOption,
  type WorkflowColumn,
  type WorkflowMonth,
  type WorkflowRow,
} from '@/pages/WorkflowV2/api';

/** Session-remembered month (YYYY-MM): going to Workflow V2 and back keeps it */
const MONTH_KEY = 'dashboard-month';

function rememberedMonth(): string | null {
  try {
    return sessionStorage.getItem(MONTH_KEY);
  } catch {
    return null;
  }
}

function rememberMonth(monthKey: string) {
  try {
    sessionStorage.setItem(MONTH_KEY, monthKey);
  } catch {
    // storage blocked: the month just resets next visit
  }
}

/** Realtime bursts (a bulk stamp, several editors) fold into one reload */
const REALTIME_DEBOUNCE_MS = 1500;

/**
 * The Dashboard's data: the Workflow V2 month list plus the selected month's
 * rows (the same single-call loader Workflow V2 uses, so service flags and
 * statuses match it exactly), the previous month's rows for the
 * month-over-month comparison, and the employee list for avatars.
 *
 * Month switches are latest-wins, and the selected month's sheet is
 * watched live: any row change reloads the numbers silently.
 */
export function useDashboardData() {
  const [months, setMonths] = useState<WorkflowMonth[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rows, setRows] = useState<WorkflowRow[]>([]);
  const [prevRows, setPrevRows] = useState<WorkflowRow[] | null>(null);
  const [columns, setColumns] = useState<WorkflowColumn[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [monthsLoading, setMonthsLoading] = useState(true);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const requestRef = useRef(0);

  useEffect(() => {
    listWorkflowMonths()
      .then(loaded => {
        setMonths(loaded);
        const remembered = rememberedMonth();
        const match = remembered
          ? loaded.find(m => m.month.startsWith(remembered))
          : undefined;
        setSelectedId((match ?? resolveCurrentMonth(loaded))?.id ?? null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setMonthsLoading(false));
    listEmployees()
      .then(setEmployees)
      .catch((e: Error) =>
        console.error('Failed to load employees:', e.message)
      );
  }, []);

  const selectedIndex = months.findIndex(m => m.id === selectedId);
  const selectedMonth = selectedIndex >= 0 ? months[selectedIndex] : null;
  // months are newest first: the previous month is the next entry
  const previousMonth =
    selectedIndex >= 0 ? (months[selectedIndex + 1] ?? null) : null;
  const previousId = previousMonth?.id ?? null;

  const load = useCallback(
    async (id: string, prevId: string | null, silent: boolean) => {
      const token = ++requestRef.current;
      if (!silent) setRowsLoading(true);
      setError(null);
      try {
        const [current, previous] = await Promise.all([
          loadWorkflowData(id),
          // The comparison is a nice-to-have: never fail the page over it
          prevId
            ? loadWorkflowData(prevId).catch(() => null)
            : Promise.resolve(null),
        ]);
        if (token !== requestRef.current) return; // superseded
        setRows(current.rows);
        setColumns(current.columns);
        setPrevRows(previous?.rows ?? null);
        setLoadedAt(new Date());
      } catch (e) {
        if (token === requestRef.current) setError((e as Error).message);
      } finally {
        if (token === requestRef.current) setRowsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (selectedId) void load(selectedId, previousId, false);
  }, [selectedId, previousId, load]);

  // Live updates: any change to the selected month's rows (a stamp, a
  // status, a new account) reloads the numbers without a spinner
  useEffect(() => {
    if (!selectedId) return;
    let timer: number | undefined;
    const channel = supabase
      .channel(`dashboard-rows-${selectedId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'spreadsheet_rows',
          filter: `spreadsheet_id=eq.${selectedId}`,
        },
        () => {
          window.clearTimeout(timer);
          timer = window.setTimeout(
            () => void load(selectedId, previousId, true),
            REALTIME_DEBOUNCE_MS
          );
        }
      )
      .subscribe();
    return () => {
      window.clearTimeout(timer);
      void supabase.removeChannel(channel);
    };
  }, [selectedId, previousId, load]);

  const selectMonth = useCallback(
    (id: string) => {
      const month = months.find(m => m.id === id);
      if (month) rememberMonth(month.month.slice(0, 7));
      setSelectedId(id);
    },
    [months]
  );

  const refresh = useCallback(() => {
    if (selectedId) void load(selectedId, previousId, true);
  }, [selectedId, previousId, load]);

  return {
    months,
    selectedMonth,
    previousMonth,
    selectMonth,
    rows,
    prevRows,
    columns,
    employees,
    /** True until the first month has loaded (skeletons, not zeros) */
    initialLoading: monthsLoading || (rowsLoading && loadedAt === null),
    loading: monthsLoading || rowsLoading,
    error,
    loadedAt,
    refresh,
  };
}
